import React, { useState, useEffect, useMemo } from 'react';
import './CategoriesManagement.css';
import CategoryForm from './CategoryForm';
import useCategories from '../hooks/useCategories';
import AdminLoading from './AdminLoading';
import useCreateCategory from '../hooks/useCreateCategory';
import { getApiUrl } from '../../config/environment';

/** useCategories builds a tree: only roots are top-level; subs are in main.children. */
function countDirectSubcategoriesInTree(tree) {
  if (!Array.isArray(tree)) return 0;
  return tree.reduce((sum, main) => sum + (main?.children?.length || 0), 0);
}

function countAllCategoryNodesInTree(tree) {
  if (!Array.isArray(tree)) return 0;
  return tree.reduce((total, main) => {
    if (!main) return total;
    let n = 1;
    for (const sub of main.children || []) {
      n += 1;
      n += sub.children?.length || 0;
    }
    return total + n;
  }, 0);
}

function getCategoryParentId(cat) {
  const p = cat?.parentCategory ?? cat?.parent;
  if (p == null || p === '') return null;
  if (typeof p === 'object') {
    const id = p._id ?? p.id;
    return id != null && String(id).trim() !== '' ? String(id).trim() : null;
  }
  const s = String(p).trim();
  return s && s !== 'null' && s !== 'undefined' ? s : null;
}

function isRootCategory(cat) {
  return !getCategoryParentId(cat);
}

const CategoriesManagement = () => {
  const { categories, loading, error, refetch } = useCategories();
  const { createCategory } = useCreateCategory();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const [filterType, setFilterType] = useState('all'); // 'all', 'main', 'subcategories'

  // Categories are now loaded automatically by the useCategories hook

  // fetchCategories function removed - now using useCategories hook

  const handleAddCategory = async (categoryData) => {
    try {
      console.log('🔧 CategoriesManagement: Adding new category:', categoryData);
      
      await createCategory(categoryData);
      console.log('✅ CategoriesManagement: Category created successfully');
      
      refetch(); // Refresh list using hook
      setShowAddForm(false);
      
      return { success: true, message: 'Category created successfully' };
    } catch (error) {
      console.error('❌ CategoriesManagement: Error adding category:', error);
      throw error;
    }
  };

  const handleUpdateCategory = async (categoryId, categoryData) => {
    try {
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      console.log('🔧 CategoriesManagement: Updating category:', categoryId, categoryData);
      
      const response = await fetch(getApiUrl(`/admin/categories/${categoryId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update category');
      }

      const result = await response.json();
      console.log('✅ CategoriesManagement: Category updated successfully:', result);
      
      refetch(); // Refresh list using hook
      setEditingCategory(null);
      
      return { success: true, message: 'Category updated successfully' };
    } catch (error) {
      console.error('❌ CategoriesManagement: Error updating category:', error);
      throw error;
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName = null, isSubcategory = false) => {
    // Find category in the list to get its details
    let categoryToDelete = null;
    let categoryType = 'category';
    
    // Search in main categories
    for (const cat of categories) {
      if (cat._id === categoryId || cat.id === categoryId) {
        categoryToDelete = cat;
        const hasParent = cat.parentCategory || cat.parent;
        categoryType = (!hasParent || hasParent === '') ? 'main category' : 'subcategory';
        break;
      }
      // Search in subcategories
      if (cat.children && Array.isArray(cat.children)) {
        for (const subcat of cat.children) {
          if (subcat._id === categoryId || subcat.id === categoryId) {
            categoryToDelete = subcat;
            categoryType = 'subcategory';
            break;
          }
        }
        if (categoryToDelete) break;
      }
    }
    
    // Use provided name or find from category
    const nameToShow = categoryName || categoryToDelete?.name || 'category';
    const typeLabel = isSubcategory ? 'subcategory' : categoryType;
    
    if (!window.confirm(`Are you sure you want to delete this ${typeLabel} "${nameToShow}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      console.log(`🔧 CategoriesManagement: Deleting ${typeLabel}:`, {
        categoryId,
        name: nameToShow,
        type: categoryType,
        isSubcategory,
      });

      const deleteUrl = getApiUrl(`/admin/categories/${categoryId}`);

      const parseDeleteBody = async (res) => {
        const text = await res.text();
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          return { success: false, message: text.slice(0, 300) };
        }
      };

      const runDelete = async (force) => {
        const url = force ? `${deleteUrl}${deleteUrl.includes('?') ? '&' : '?'}force=true` : deleteUrl;
        const res = await fetch(url, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        const body = await parseDeleteBody(res);
        return { response: res, result: body };
      };

      const finishSuccess = async (result) => {
        if (result.success === false) {
          const errMsg = result.message || result.error || `Failed to delete ${typeLabel}`;
          console.error(`❌ Delete success=false for ${typeLabel}:`, result);
          alert(`❌ ${errMsg}`);
          return;
        }
        const dc = result.data?.deletedCategory;
        const hardDelete =
          dc &&
          dc.deleted === true &&
          dc.permanent === true;
        const successMsg =
          result.message ||
          (hardDelete
            ? `"${nameToShow}" permanently removed from the database.`
            : `"${nameToShow}" removed.`);
        console.log(`✅ Category hard-delete OK (${typeLabel}):`, {
          deletedCategory: dc,
          success: result.success,
        });
        alert(`✅ ${successMsg}`);
        await refetch();
      };

      let { response, result } = await runDelete(false);
      console.log('📥 Delete response:', response.status, result);

      if (response.ok && (response.status === 200 || response.status === 204)) {
        if (result.success === false) {
          const errorMessage = result.message || result.error || `Failed to delete ${typeLabel}`;
          alert(`❌ ${errorMessage}`);
          return;
        }
        await finishSuccess(result);
        return;
      }

      if (response.status === 400) {
        const errorMessage = result.message || result.error || `Cannot delete ${typeLabel}`;
        console.error(`❌ Delete failed (400) for ${typeLabel}:`, errorMessage);
        const hint =
          categoryType === 'subcategory'
            ? 'Please remove linked products and children first, or use force delete if allowed.'
            : 'Please remove linked products and subcategories first, or use force delete if allowed.';
        const tryForce = window.confirm(`❌ ${errorMessage}\n\n${hint}\n\nTry force delete? (Admin — removes category from DB even with children/products.)`);
        if (tryForce) {
          const second = await runDelete(true);
          console.log('📥 Delete (force) response:', second.response.status, second.result);
          if (second.response.ok && (second.response.status === 200 || second.response.status === 204)) {
            if (second.result.success === false) {
              alert(`❌ ${second.result.message || second.result.error || 'Force delete failed'}`);
              return;
            }
            await finishSuccess(second.result);
            return;
          }
          const err2 =
            second.result.message ||
            second.result.error ||
            `HTTP ${second.response.status}`;
          alert(`❌ ${err2}`);
        }
        return;
      }

      if (response.status === 404) {
        const errorMessage = result.message || result.error || `${typeLabel} not found`;
        console.error(`❌ Delete failed (404) for ${typeLabel}:`, errorMessage);
        alert(`❌ ${errorMessage}`);
        return;
      }

      const errorMessage = result.message || result.error || `HTTP ${response.status}: Failed to delete ${typeLabel}`;
      console.error(`❌ Delete failed for ${typeLabel}:`, errorMessage);
      alert(`❌ ${errorMessage}`);
    } catch (error) {
      console.error(`❌ CategoriesManagement: Error deleting ${typeLabel}:`, error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        categoryId: categoryId,
        categoryName: nameToShow,
        categoryType: typeLabel
      });
      
      // Handle network errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        alert('❌ Network error: Backend server se connect nahi ho raha. Please check:\n1. Backend server running hai\n2. API URL sahi hai');
      } else {
        const errorMsg = categoryType === 'subcategory' 
          ? `❌ Error deleting ${typeLabel}: ${error.message}\n\nPlease check:\n1. Subcategory has no products\n2. Subcategory has no children categories\n3. Backend server is running`
          : `❌ Error deleting ${typeLabel}: ${error.message}\n\nPlease check:\n1. Category has no products\n2. Category has no subcategories\n3. Backend server is running`;
        alert(errorMsg);
      }
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleSubcategoryExpansion = (subcategoryId) => {
    const newExpanded = new Set(expandedSubcategories);
    if (newExpanded.has(subcategoryId)) {
      newExpanded.delete(subcategoryId);
    } else {
      newExpanded.add(subcategoryId);
    }
    setExpandedSubcategories(newExpanded);
  };

  // Main categories only - for tree view (root level), with search + filter
  const mainCategoriesForTree = useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];
    let mains = categories.filter((cat) => cat && typeof cat === 'object' && isRootCategory(cat));
    if (filterType === 'subcategories') {
      mains = mains.filter((m) => m.children && m.children.length > 0);
    }
    if (searchTerm && searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const matches = (c) => (c?.name || '').toLowerCase().includes(q) || (c?.description || '').toLowerCase().includes(q);
      mains = mains.filter(main => {
        if (matches(main)) return true;
        if (main.children?.some(sub => matches(sub))) return true;
        if (main.children?.some(sub => sub.children?.some(ch => matches(ch)))) return true;
        return false;
      });
    }
    return mains;
  }, [categories, searchTerm, filterType]);

  const subcategoriesForTree = useMemo(() => {
    if (!categories || !Array.isArray(categories) || filterType !== 'subcategories') return [];
    let subs = [];
    for (const main of categories) {
      for (const sub of main?.children || []) {
        if (sub) subs.push(sub);
      }
    }
    if (searchTerm && searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const matches = (c) =>
        (c?.name || '').toLowerCase().includes(q) ||
        (c?.description || '').toLowerCase().includes(q);
      subs = subs.filter(
        (sub) =>
          matches(sub) || (sub.children || []).some((ch) => matches(ch))
      );
    }
    return subs;
  }, [categories, searchTerm, filterType]);

  const filteredCategories = useMemo(() => {
    console.log('🔍 filteredCategories useMemo - Categories:', categories);
    console.log('🔍 filteredCategories useMemo - Categories is Array:', Array.isArray(categories));
    console.log('🔍 filteredCategories useMemo - Categories length:', categories?.length || 0);
    console.log('🔍 filteredCategories useMemo - Search term:', searchTerm);
    console.log('🔍 filteredCategories useMemo - Filter type:', filterType);
    
    // Ensure categories is an array
    if (!categories) {
      console.warn('⚠️ CategoriesManagement - Categories is null/undefined');
      return [];
    }
    
    if (!Array.isArray(categories)) {
      console.warn('⚠️ CategoriesManagement - Categories is not an array:', categories);
      console.warn('⚠️ CategoriesManagement - Categories type:', typeof categories);
      return [];
    }
    
    let filtered = [...categories];
    
    // Apply filter type
    if (filterType === 'main') {
      // Show only main categories (categories without parent)
      filtered = filtered.filter((category) => category && typeof category === 'object' && isRootCategory(category));
    } else if (filterType === 'subcategories') {
      // Tree only has roots at top level — flatten subs for filter/list length
      filtered = [];
      for (const main of categories) {
        for (const sub of main?.children || []) {
          filtered.push(sub);
        }
      }
    }
    // 'all' shows all categories, no additional filtering needed
    
    // Apply search term filter
    if (searchTerm && searchTerm.trim() !== '') {
    const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(category => {
      // Validate category object
      if (!category || typeof category !== 'object') {
        console.warn('⚠️ Invalid category object:', category);
        return false;
      }
      
      const name = (category.name || '').toLowerCase();
      const description = (category.description || '').toLowerCase();
      
      const nameMatch = name.includes(searchLower);
      const descMatch = description.includes(searchLower);
      
      let childrenMatch = false;
      if (category.children && Array.isArray(category.children)) {
        childrenMatch = category.children.some(child => {
          if (!child || typeof child !== 'object') return false;
          const childName = (child.name || '').toLowerCase();
          const childDesc = (child.description || '').toLowerCase();
          return childName.includes(searchLower) || childDesc.includes(searchLower);
        });
      }
      
      return nameMatch || descMatch || childrenMatch;
    });
    }
    
    console.log('🔍 CategoriesManagement - Filtered categories:', filtered.length, 'out of', categories.length);
    return filtered;
  }, [categories, searchTerm, filterType]);

  const getMainCategoriesCount = () => {
    if (!Array.isArray(categories)) {
      return 0;
    }
    return categories.filter((category) => category && typeof category === 'object' && isRootCategory(category))
      .length;
  };

  const getSubcategoriesCount = () => countDirectSubcategoriesInTree(categories);

  const getTotalCategoriesCount = () => countAllCategoryNodesInTree(categories);

  // Debug: Log categories data
  useEffect(() => {
    console.log('📦 CategoriesManagement - Categories:', categories);
    console.log('📦 CategoriesManagement - Categories length:', categories?.length || 0);
    console.log('📦 CategoriesManagement - Categories type:', Array.isArray(categories) ? 'Array' : typeof categories);
    console.log('📦 CategoriesManagement - Is Array:', Array.isArray(categories));
    console.log('📦 CategoriesManagement - Filtered length:', filteredCategories?.length || 0);
    console.log('📦 CategoriesManagement - Filtered is Array:', Array.isArray(filteredCategories));
    console.log('📦 CategoriesManagement - Loading:', loading);
    console.log('📦 CategoriesManagement - Error:', error);
    console.log('📦 CategoriesManagement - Search term:', searchTerm);
    
    // Log first category if exists
    if (categories && Array.isArray(categories) && categories.length > 0) {
      console.log('📦 First category sample:', JSON.stringify(categories[0], null, 2));
    } else {
      console.warn('⚠️ CategoriesManagement - No categories or categories is not an array');
      console.warn('⚠️ Categories value:', categories);
      console.warn('⚠️ Categories type:', typeof categories);
    }
  }, [categories, loading, error, filteredCategories, searchTerm]);

  if (loading && (!categories || !Array.isArray(categories) || categories.length === 0)) {
    return (
      <AdminLoading
        title="Loading Categories"
        subtitle="Fetching category tree..."
        icon="📁"
      />
    );
  }

  return (
    <div className="categories-management">
      {/* Header — catalog hero */}
      <div className="header-section">
        <div className="cm-hero-card">
          <div className="cm-hero-main">
            <div className="cm-hero-icon-wrap" aria-hidden="true">
              <i className="fas fa-sitemap" />
            </div>
            <div className="cm-hero-copy">
              <p className="cm-hero-eyebrow">Admin</p>
              <h1 className="cm-hero-title">Categories Management</h1>
              <p className="cm-hero-desc">
                Organize and manage your product categories
              </p>
            </div>
          </div>
          <div className="cm-hero-cta">
            <button
              type="button"
              className="cm-btn-add-category"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus" aria-hidden="true" />
              <span>Add new category</span>
            </button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="stats-grid">
          <div 
            className={`stat-card stat-total ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
            style={{ cursor: 'pointer' }}
            title="Click to show all categories"
          >
            <div className="stat-icon total-categories">
              <i className="fas fa-sitemap"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{getTotalCategoriesCount()}</div>
              <div className="stat-label">Total Categories</div>
            </div>
          </div>
          <div 
            className={`stat-card stat-main ${filterType === 'main' ? 'active' : ''}`}
            onClick={() => setFilterType(filterType === 'main' ? 'all' : 'main')}
            style={{ cursor: 'pointer' }}
            title="Click to filter main categories"
          >
            <div className="stat-icon main-categories">
              <i className="fas fa-folder-open"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{getMainCategoriesCount()}</div>
              <div className="stat-label">Main Categories</div>
            </div>
          </div>
          <div 
            className={`stat-card stat-sub ${filterType === 'subcategories' ? 'active' : ''}`}
            onClick={() => setFilterType(filterType === 'subcategories' ? 'all' : 'subcategories')}
            style={{ cursor: 'pointer' }}
            title="Click to filter categories with subcategories"
          >
            <div className="stat-icon subcategories">
              <i className="fas fa-folder"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">
                {getSubcategoriesCount()}
              </div>
              <div className="stat-label">Subcategories</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Search categories by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {(searchTerm || filterType !== 'all') && (
            <div className="search-results-badge">
              <i className="fas fa-filter"></i>
              <span>
                {(filterType === 'subcategories' ? subcategoriesForTree.length : mainCategoriesForTree.length)} result
                {(filterType === 'subcategories' ? subcategoriesForTree.length : mainCategoriesForTree.length) !== 1 ? 's' : ''} found
                {filterType !== 'all' && !searchTerm && (
                  <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                    ({filterType === 'main' ? 'Main Categories' : 'With Subcategories'})
                  </span>
                )}
              </span>
              {(searchTerm || filterType !== 'all') && (
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                  }}
                  className="clear-filter-btn"
                  title="Clear all filters"
                  style={{
                    marginLeft: '10px',
                    background: 'rgba(255, 215, 0, 0.2)',
                    border: '1px solid rgba(255, 215, 0, 0.4)',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    color: '#8b6914',
                    fontSize: '0.75rem'
                  }}
                >
                  <i className="fas fa-times"></i> Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Categories List */}
      <div className="categories-container">
        {/* Loading State - when refreshing with existing data */}
        {loading && categories?.length > 0 && (
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p>Updating categories...</p>
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Unable to Load Categories</h3>
            <p>{error}</p>
            <button onClick={refetch} className="btn-retry">
              <i className="fas fa-redo"></i>
              Try Again
            </button>
          </div>
        )}
        
        {/* Category Tree View - Root structure */}
        {!loading && !error && categories && Array.isArray(categories) && categories.length > 0 && (
          <div className="category-tree">
            <div className="tree-header">
              <span className="tree-col-thumb">Thumbnail</span>
              <span className="tree-col-name">Name</span>
              <span className="tree-col-type">Type</span>
              <span className="tree-col-actions">Actions</span>
            </div>
            {filterType === 'subcategories' && subcategoriesForTree.length === 0 && (
              <div className="empty-state empty-state--inline">
                <p>No subcategories match this filter.</p>
              </div>
            )}

            {filterType === 'subcategories' &&
              subcategoriesForTree.map((subCat) => {
                const subId = subCat._id || subCat.id || Math.random();
                const hasChildren = subCat.children && subCat.children.length > 0;
                const isSubExpanded = expandedSubcategories.has(subId);
                const subImg = subCat.image || subCat.images?.[0]?.url || subCat.images?.[0];
                return (
                  <div key={subId} className="tree-branch">
                    <div className="tree-row tree-sub">
                      <div className="tree-thumb">
                        {subImg ? (
                          <img src={subImg} alt={subCat.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement?.querySelector('.tree-thumb-placeholder')?.classList.add('show'); }} />
                        ) : null}
                        <div className={`tree-thumb-placeholder sub ${!subImg ? 'show' : ''}`}>
                          <i className="fas fa-folder"></i>
                        </div>
                      </div>
                      <div className="tree-name">
                        {hasChildren ? (
                          <button className="tree-expand" onClick={() => toggleSubcategoryExpansion(subId)} title={isSubExpanded ? 'Collapse' : 'Expand'}>
                            <i className={`fas fa-chevron-${isSubExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        ) : (
                          <span className="tree-expand-placeholder"></span>
                        )}
                        <span className="tree-label">{subCat.name}</span>
                        {subCat.isFeatured && <span className="tree-badge featured">★</span>}
                      </div>
                      <div className="tree-type"><span className="type-badge sub">Sub</span></div>
                      <div className="tree-actions">
                        <button onClick={() => setEditingCategory(subCat)} title="Edit"><i className="fas fa-edit"></i></button>
                        <button onClick={() => setEditingCategory({ ...subCat, imageAction: 'upload' })} title="Image"><i className="fas fa-image"></i></button>
                        <button onClick={() => handleDeleteCategory(subId, subCat.name, true)} title="Delete" className="btn-del"><i className="fas fa-trash-alt"></i></button>
                      </div>
                    </div>
                    {hasChildren && isSubExpanded && subCat.children.map((childCat) => {
                      const childId = childCat._id || childCat.id || Math.random();
                      const childImg = childCat.image || childCat.images?.[0]?.url || childCat.images?.[0];
                      return (
                        <div key={childId} className="tree-children">
                          <div className="tree-row tree-child">
                            <div className="tree-thumb">
                              {childImg ? (
                                <img src={childImg} alt={childCat.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement?.querySelector('.tree-thumb-placeholder')?.classList.add('show'); }} />
                              ) : null}
                              <div className={`tree-thumb-placeholder child ${!childImg ? 'show' : ''}`}>
                                <i className="fas fa-tag"></i>
                              </div>
                            </div>
                            <div className="tree-name">
                              <span className="tree-expand-placeholder"></span>
                              <span className="tree-label">{childCat.name}</span>
                              {childCat.isFeatured && <span className="tree-badge featured">★</span>}
                            </div>
                            <div className="tree-type"><span className="type-badge child">Child</span></div>
                            <div className="tree-actions">
                              <button onClick={() => setEditingCategory(childCat)} title="Edit"><i className="fas fa-edit"></i></button>
                              <button onClick={() => setEditingCategory({ ...childCat, imageAction: 'upload' })} title="Image"><i className="fas fa-image"></i></button>
                              <button onClick={() => handleDeleteCategory(childId, childCat.name, true)} title="Delete" className="btn-del"><i className="fas fa-trash-alt"></i></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}

            {filterType !== 'subcategories' && mainCategoriesForTree.map((mainCat) => {
              const mainId = mainCat._id || mainCat.id || Math.random();
              const hasSubs = mainCat.children && mainCat.children.length > 0;
              const isMainExpanded = expandedCategories.has(mainId);
              const mainImg = mainCat.image || mainCat.images?.[0]?.url || mainCat.images?.[0];
              return (
                <div key={mainId} className="tree-branch">
                  {/* Main Category Row */}
                  <div className="tree-row tree-main">
                    <div className="tree-thumb">
                      {mainImg ? (
                        <img src={mainImg} alt={mainCat.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement?.querySelector('.tree-thumb-placeholder')?.classList.add('show'); }} />
                      ) : null}
                      <div className={`tree-thumb-placeholder main ${!mainImg ? 'show' : ''}`}>
                        <i className="fas fa-folder-open"></i>
                      </div>
                    </div>
                    <div className="tree-name">
                      {hasSubs && (
                        <button className="tree-expand" onClick={() => toggleCategoryExpansion(mainId)} title={isMainExpanded ? 'Collapse' : 'Expand'}>
                          <i className={`fas fa-chevron-${isMainExpanded ? 'down' : 'right'}`}></i>
                        </button>
                      )}
                      <span className="tree-label">{mainCat.name}</span>
                      {mainCat.isFeatured && <span className="tree-badge featured">★</span>}
                    </div>
                    <div className="tree-type"><span className="type-badge main">Main</span></div>
                    <div className="tree-actions">
                      <button onClick={() => setEditingCategory(mainCat)} title="Edit"><i className="fas fa-edit"></i></button>
                      <button onClick={() => setEditingCategory({...mainCat, imageAction: 'upload'})} title="Image"><i className="fas fa-image"></i></button>
                      <button onClick={() => handleDeleteCategory(mainId, mainCat.name, false)} title="Delete" className="btn-del"><i className="fas fa-trash-alt"></i></button>
                    </div>
                  </div>
                  {/* Subcategories - Level 2 (hidden when "Main categories" filter is active) */}
                  {filterType !== 'main' && hasSubs && isMainExpanded && mainCat.children.map((subCat) => {
                    const subId = subCat._id || subCat.id || Math.random();
                    const hasChildren = subCat.children && subCat.children.length > 0;
                    const isSubExpanded = expandedSubcategories.has(subId);
                    const subImg = subCat.image || subCat.images?.[0]?.url || subCat.images?.[0];
                    return (
                      <div key={subId} className="tree-children">
                        <div className="tree-row tree-sub">
                          <div className="tree-thumb">
                            {subImg ? (
                              <img src={subImg} alt={subCat.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement?.querySelector('.tree-thumb-placeholder')?.classList.add('show'); }} />
                            ) : null}
                            <div className={`tree-thumb-placeholder sub ${!subImg ? 'show' : ''}`}>
                              <i className="fas fa-folder"></i>
                            </div>
                          </div>
                          <div className="tree-name">
                            {hasChildren ? (
                              <button className="tree-expand" onClick={() => toggleSubcategoryExpansion(subId)} title={isSubExpanded ? 'Collapse' : 'Expand'}>
                                <i className={`fas fa-chevron-${isSubExpanded ? 'down' : 'right'}`}></i>
                              </button>
                            ) : <span className="tree-expand-placeholder"></span>}
                            <span className="tree-label">{subCat.name}</span>
                            {subCat.isFeatured && <span className="tree-badge featured">★</span>}
                          </div>
                          <div className="tree-type"><span className="type-badge sub">Sub</span></div>
                          <div className="tree-actions">
                            <button onClick={() => setEditingCategory(subCat)} title="Edit"><i className="fas fa-edit"></i></button>
                            <button onClick={() => setEditingCategory({...subCat, imageAction: 'upload'})} title="Image"><i className="fas fa-image"></i></button>
                            <button onClick={() => handleDeleteCategory(subId, subCat.name, true)} title="Delete" className="btn-del"><i className="fas fa-trash-alt"></i></button>
                          </div>
                        </div>
                        {/* Children - Level 3 */}
                        {hasChildren && isSubExpanded && subCat.children.map((childCat) => {
                          const childId = childCat._id || childCat.id || Math.random();
                          const childImg = childCat.image || childCat.images?.[0]?.url || childCat.images?.[0];
                          return (
                            <div key={childId} className="tree-children">
                              <div className="tree-row tree-child">
                                <div className="tree-thumb">
                                  {childImg ? (
                                    <img src={childImg} alt={childCat.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement?.querySelector('.tree-thumb-placeholder')?.classList.add('show'); }} />
                                  ) : null}
                                  <div className={`tree-thumb-placeholder child ${!childImg ? 'show' : ''}`}>
                                    <i className="fas fa-tag"></i>
                                  </div>
                                </div>
                                <div className="tree-name">
                                  <span className="tree-expand-placeholder"></span>
                                  <span className="tree-label">{childCat.name}</span>
                                  {childCat.isFeatured && <span className="tree-badge featured">★</span>}
                                </div>
                                <div className="tree-type"><span className="type-badge child">Child</span></div>
                                <div className="tree-actions">
                                  <button onClick={() => setEditingCategory(childCat)} title="Edit"><i className="fas fa-edit"></i></button>
                                  <button onClick={() => setEditingCategory({...childCat, imageAction: 'upload'})} title="Image"><i className="fas fa-image"></i></button>
                                  <button onClick={() => handleDeleteCategory(childId, childCat.name, true)} title="Delete" className="btn-del"><i className="fas fa-trash-alt"></i></button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && (!categories || !Array.isArray(categories) || categories.length === 0) && (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-folder-open"></i>
            </div>
            <h3>No Categories Yet</h3>
            <p>Get started by creating your first category to organize your products.</p>
            <button 
              className="btn-add-first"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus-circle"></i>
              <span>Create First Category</span>
            </button>
          </div>
        )}
      </div>

      {/* Add Category Form */}
      {showAddForm && (
        <CategoryForm
          onSubmit={handleAddCategory}
          onCancel={() => setShowAddForm(false)}
          categories={categories}
        />
      )}

      {/* Edit Category Form */}
      {editingCategory && (
        <CategoryForm
          category={editingCategory}
          onSubmit={(data) => handleUpdateCategory(editingCategory._id, data)}
          onCancel={() => setEditingCategory(null)}
          categories={categories}
        />
      )}
    </div>
  );
};

export default CategoriesManagement;
